import React, { useState, useEffect } from "react";
import { useRoomMode } from "@/context/RoomModeContext";
import { Room } from "@/types/rooms";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MessageCircle, BookCheck, Plus, ChevronDown, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/context/LanguageContext";

interface RoomHeaderProps {
  room: Room | null;
  onOpenTutor: () => void;
  isOwnerOrMember: boolean;
  isTutorOpen: boolean;
}
export const RoomHeader: React.FC<RoomHeaderProps> = ({
  room,
  onOpenTutor,
  isOwnerOrMember,
  isTutorOpen
}) => {
  const navigate = useNavigate();
  const { t, isRtl } = useTranslation();
  const {
    mode,
    setMode
  } = useRoomMode();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState(room?.name ?? "");
  const [editedDescription, setEditedDescription] = useState(room?.description ?? "");
  const [exams, setExams] = useState<Array<{
    id: string;
    title: string;
    created_at: string;
  }>>([]);
  useEffect(() => {
    if (room?.id) {
      fetchExams();
    }
  }, [room?.id]);
  const fetchExams = async () => {
    if (!room?.id) return;
    const {
      data,
      error
    } = await supabase.from("exams").select("id, title, created_at").eq("room_id", room.id).order("created_at", {
      ascending: true
    });
    if (!error && data) {
      setExams(data);
    }
  };
  const handleDeleteExam = async (examId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const {
        error
      } = await supabase.from("exams").delete().eq("id", examId);
      if (error) throw error;
      setExams(prev => prev.filter(exam => exam.id !== examId));
      toast.success("Exam deleted successfully");
    } catch (error) {
      console.error("Error deleting exam:", error);
      toast.error("Failed to delete exam");
    }
  };
  const toggleAddContent = () => {
    setMode(mode === "add-content" ? "default" : "add-content");
  };
  const startExamCreation = () => {
    const isExamMode = mode === "exam" || mode === "exam-step-2" || mode === "exam-step-3" || mode === "exam-loading";
    setMode(isExamMode ? "default" : "exam");
  };
  const handleSaveName = async () => {
    if (!room || !editedName.trim()) {
      toast.error("Room name cannot be empty");
      setEditedName(room?.name ?? "");
      setIsEditingName(false);
      return;
    }
    try {
      const {
        error
      } = await supabase.from("rooms").update({
        name: editedName.trim()
      }).eq("id", room.id);
      if (error) throw error;
      toast.success("Room name updated");
      setIsEditingName(false);
    } catch (error) {
      console.error("Error updating room name:", error);
      toast.error("Failed to update room name");
      setEditedName(room?.name ?? "");
      setIsEditingName(false);
    }
  };
  const handleSaveDescription = async () => {
    if (!room) return;
    try {
      const {
        error
      } = await supabase.from("rooms").update({
        description: editedDescription.trim() || null
      }).eq("id", room.id);
      if (error) throw error;
      toast.success("Description updated");
      setIsEditingDescription(false);
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error("Failed to update description");
      setEditedDescription(room?.description ?? "");
      setIsEditingDescription(false);
    }
  };
  const handleCancelName = () => {
    setEditedName(room?.name ?? "");
    setIsEditingName(false);
  };
  const handleCancelDescription = () => {
    setEditedDescription(room?.description ?? "");
    setIsEditingDescription(false);
  };
  const examButtonLabel = mode === "exam" || mode === "exam-step-2" || mode === "exam-step-3" || mode === "exam-loading" 
    ? t("room.closeExam") 
    : t("room.createExam");

  return <div className="w-full text-foreground">
      <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-start">
        <div className="flex flex-1 flex-col min-w-0">
          <div className="mb-2 flex w-full items-center text-2xl md:text-3xl font-medium">
            {isEditingName ? <input type="text" value={editedName} onChange={e => setEditedName(e.target.value)} onKeyDown={e => {
            if (e.key === "Enter") handleSaveName();
            if (e.key === "Escape") handleCancelName();
          }} onBlur={handleSaveName} className="flex-1 bg-transparent px-1 py-0.5 text-2xl md:text-3xl focus:outline-none" dir={isRtl ? "rtl" : "ltr"} autoFocus maxLength={100} /> : <button onClick={() => isOwnerOrMember && setIsEditingName(true)} className={`truncate text-left px-1 py-0.5 transition-colors ${isOwnerOrMember ? "cursor-text hover:bg-muted/50 rounded-sm" : "cursor-default"}`} dir={isRtl ? "rtl" : "ltr"} disabled={!isOwnerOrMember}>
                {room?.name ?? t("room.untitledRoom")}
              </button>}
          </div>
          
          <div className="mt-1 w-full text-muted-foreground">
            {isEditingDescription ? <textarea value={editedDescription} onChange={e => setEditedDescription(e.target.value)} onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSaveDescription();
            }
            if (e.key === "Escape") handleCancelDescription();
          }} onBlur={handleSaveDescription} className="w-full bg-transparent px-1 py-0.5 text-sm md:text-base focus:outline-none resize-none" dir={isRtl ? "rtl" : "ltr"} autoFocus rows={2} maxLength={500} placeholder={t("room.addDescription")} /> : <button onClick={() => isOwnerOrMember && setIsEditingDescription(true)} className={`text-left px-1 py-0.5 transition-colors ${isOwnerOrMember ? "cursor-text hover:bg-muted/50 rounded-sm" : "cursor-default"}`} dir={isRtl ? "rtl" : "ltr"} disabled={!isOwnerOrMember}>
                {room?.description ? <span className="line-clamp-2 text-sm md:text-base">{room.description}</span> : isOwnerOrMember ? <span className="text-sm md:text-base italic opacity-50">{t("room.addDescription")}</span> : null}
              </button>}
          </div>

          {/* Buttons under description - show on tablet, move to right on desktop */}
          {isOwnerOrMember && !isTutorOpen && <div className={`mt-4 flex flex-wrap justify-end gap-3 md:flex xl:hidden xl:justify-start overflow-x-auto ${isRtl ? "flex-row" : "flex-row-reverse"}`}>
              <div className={`flex flex-wrap justify-end gap-3 text-sm font-medium pb-2 ${isRtl ? "flex-row" : "flex-row-reverse"}`}>
                <Button onClick={toggleAddContent} size="sm" className={`h-10 gap-1.5 ${mode === "add-content" ? "opacity-70" : ""} ${isRtl ? "flex-row-reverse" : ""}`}>
                  <Plus className="h-4 w-4" />
                  <span>{t("room.addContent")}</span>
                </Button>

                <Button onClick={onOpenTutor} variant="outline" size="sm" className={`h-10 gap-0 sm:gap-1 ${isRtl ? "flex-row-reverse" : ""}`}>
                  <MessageCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">{t("room.roomChat")}</span>
                </Button>

                {exams.length > 0 ? <ButtonGroup>
                    <Button onClick={startExamCreation} variant="outline" size="sm" className={`h-10 gap-2 ${isRtl ? "flex-row-reverse" : ""}`}>
                      <BookCheck className="h-4 w-4" />
                      <span className="hidden sm:inline">{examButtonLabel}</span>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-10 !pl-2 !pr-2">
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="[--radius:1rem]">
                        <DropdownMenuGroup>
                          {exams.map((exam, index) => <DropdownMenuItem key={exam.id} onClick={() => navigate(`/room/${room?.id}/exam/${exam.id}`)} className="gap-1">
                              <BookCheck className="h-4 w-4" />
                              {exam.title || `Exam ${exams.length - index}`}
                            </DropdownMenuItem>)}
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </ButtonGroup> : <Button onClick={startExamCreation} variant="outline" size="sm" className={`h-10 gap-2 ${isRtl ? "flex-row-reverse" : ""}`}>
                    <BookCheck className="h-4 w-4" />
                    <span className="hidden sm:inline">{examButtonLabel}</span>
                  </Button>}
              </div>
            </div>}

          {/* Buttons under description when tutor panel is open */}
          {isOwnerOrMember && isTutorOpen && <div className="mt-4 overflow-x-auto xl:overflow-x-visible">
              <div className={`flex items-center gap-2 text-sm font-medium pb-2 xl:pb-0 ${isRtl ? "flex-row-reverse" : ""}`}>
                <Button onClick={onOpenTutor} variant="outline" size="sm" className={`h-9 gap-1.5 ${isRtl ? "flex-row-reverse" : ""}`}>
                  <MessageCircle className="h-3.5 w-3.5" />
                  <span className="text-xs whitespace-nowrap">{t("room.roomChat")}</span>
                </Button>

                {exams.length > 0 ? <ButtonGroup>
                    <Button onClick={startExamCreation} variant="outline" size="sm" className={`h-9 gap-1.5 ${isRtl ? "flex-row-reverse" : ""}`}>
                      <BookCheck className="h-3.5 w-3.5" />
                      <span className="text-xs">{examButtonLabel}</span>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-9 !pl-1.5 !pr-1.5">
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="[--radius:1rem]">
                        <DropdownMenuGroup>
                          {exams.map((exam, index) => <DropdownMenuItem key={exam.id} onClick={() => navigate(`/room/${room?.id}/exam/${exam.id}`)} className="gap-1">
                              <BookCheck className="h-4 w-4" />
                              {exam.title || `Exam ${exams.length - index}`}
                            </DropdownMenuItem>)}
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </ButtonGroup> : <Button onClick={startExamCreation} variant="outline" size="sm" className={`h-9 gap-1.5 ${isRtl ? "flex-row-reverse" : ""}`}>
                    <BookCheck className="h-3.5 w-3.5" />
                    <span className="text-xs">{examButtonLabel}</span>
                  </Button>}

                <Button onClick={toggleAddContent} size="sm" className={`h-9 gap-1.5 ${mode === "add-content" ? "opacity-70" : ""} ${isRtl ? "flex-row-reverse" : ""}`}>
                  <Plus className="h-3.5 w-3.5" />
                  <span className="text-xs whitespace-nowrap">{t("room.addContent")}</span>
                </Button>
              </div>
            </div>}
        </div>

        {/* Original button position - only visible on desktop */}
        {isOwnerOrMember && !isTutorOpen && <div className={`mt-4 mb-2 flex flex-col text-sm font-medium xl:mt-0 xl:mb-0 xl:ml-4 xl:justify-end ${isRtl ? "xl:mr-4 xl:ml-0" : ""}`}>
            <div className={`hidden xl:flex flex-wrap items-center gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
              <Button onClick={onOpenTutor} variant="outline" className={`w-36 ${isRtl ? "flex-row-reverse" : ""}`}>
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">{t("room.roomChat")}</span>
              </Button>

              {exams.length > 0 ? <ButtonGroup>
                  <Button onClick={startExamCreation} variant="outline" className={`w-32 ${isRtl ? "flex-row-reverse" : ""}`}>
                    <BookCheck className="h-4 w-4" />
                    <span className="hidden sm:inline">{examButtonLabel}</span>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="!pl-2 !pr-2">
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="[--radius:1rem]">
                      <DropdownMenuGroup>
                        {exams.map((exam, index) => <DropdownMenuItem key={exam.id} onClick={() => navigate(`/room/${room?.id}/exam/${exam.id}/summary`)} className="gap-2 justify-between">
                            <span>Exam {index + 1}</span>
                            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" onClick={e => handleDeleteExam(exam.id, e)} />
                          </DropdownMenuItem>)}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ButtonGroup> : <Button onClick={startExamCreation} variant="outline" className={`w-32 ${isRtl ? "flex-row-reverse" : ""}`}>
                  <BookCheck className="h-4 w-4" />
                  <span className="hidden sm:inline">{examButtonLabel}</span>
                </Button>}

              <Button onClick={toggleAddContent} className={`w-32 ${mode === "add-content" ? "opacity-70" : ""} ${isRtl ? "flex-row-reverse" : ""}`}>
                <Plus className="h-4 w-4" />
                {t("room.addContent")}
              </Button>
            </div>
          </div>}
      </div>

      <div className="w-full font-medium">
        <div className="mt-4 flex items-center justify-between border-b border-border/60 md:mt-8">
          <div className={`flex items-center text-sm ${isRtl ? "flex-row-reverse" : ""}`}>
            <button className={`relative h-8 w-[62px] cursor-pointer overflow-visible pb-2 font-medium text-foreground ${isRtl ? "text-right" : "text-center"}`}>
              {t("room.sourcesTab")}
              <div className="absolute inset-x-0 bottom-0 h-0.5 bg-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};